import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, Search, ChevronLeft, ChevronRight,
  Clock4, XCircle,
} from 'lucide-react';

import { usePermission }   from '@/hooks/usePermission';
import { Permissions }     from '@/constants/permissions';
import { AppRoutes }       from '@/constants/routes';
import { Spinner }         from '@/components/ui/Spinner';
import { ConfirmDialog }   from '@/components/ui/ConfirmDialog';
import { cn }              from '@/utils/cn';
import { apiClient }       from '@/api/client';

// â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
interface AccrualListDto {
  id: string;
  scheduleCode: string;
  description: string;
  accrualType: string;
  totalAmount: number;
  currencyCode: string;
  totalPeriods: number;
  periodsRecognized: number;
  recognitionFrequency: string;
  startDate: string;
  endDate?: string | null;
  status: string;
}

interface AccrualFilter {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  accrualType?: string;
}

const STATUS_COLORS: Record<string, string> = {
  Active:    'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Completed: 'bg-teal-100 dark:bg-teal-900/30  text-teal-700 dark:text-teal-400',
  Cancelled: 'bg-red-100 dark:bg-red-900/30   text-red-700 dark:text-red-400',
  Draft:     'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
};

const ACCRUAL_TYPES = ['Prepayment', 'Accrued Expense', 'Accrued Revenue', 'Deferred Revenue'];
const STATUSES      = ['Draft', 'Active', 'Completed', 'Cancelled'];

// â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const AccrualListPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();

  const canCreate = hasPermission(Permissions.ACCRUAL_CREATE);
  const canDelete = hasPermission(Permissions.ACCRUAL_DELETE);
  const canView   = hasPermission(Permissions.ACCRUAL_VIEW);

  const [items,   setItems]   = useState<AccrualListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [filter, setFilter] = useState<AccrualFilter>({ page: 1, pageSize });
  const [search, setSearch] = useState('');

  const [cancelling, setCancelling] = useState<AccrualListDto | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const load = useCallback(async (f: AccrualFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page',     String(f.page));
      params.set('pageSize', String(f.pageSize));
      if (f.search)      params.set('search',      f.search);
      if (f.status)      params.set('status',      f.status);
      if (f.accrualType) params.set('accrualType', f.accrualType);

      const res  = await apiClient.get(`/accruals?${params}`);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to load accruals', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const handleSearch = () => {
    setFilter(f => ({ ...f, search: search || undefined, page: 1 }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilter(f => ({ ...f, page: newPage }));
  };

  const handleCancel = (item: AccrualListDto) => setCancelling(item);

  const executeCancel = async () => {
    if (!cancelling) return;
    setCancelLoading(true);
    try {
      await apiClient.post(`/accruals/${cancelling.id}/cancel`, { reason: 'Cancelled by user' });
      setCancelling(null);
      load(filter);
    } catch (err) {
      console.error('Cancel failed', err);
    } finally {
      setCancelLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  // A missing date is a dash, not the words "Invalid Date"; a missing amount is a dash, not a crash.
  // A grid should degrade one cell at a time.
  const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    const parsed = new Date(d);
    return Number.isNaN(parsed.getTime())
      ? '—'
      : parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const fmtAmt = (n?: number | null, ccy = '') =>
    n == null ? '—' : `${ccy} ${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Clock4 size={20} className="text-teal-600 dark:text-teal-400" />
              Accruals
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total.toLocaleString()} schedules
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(filter)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors" title="Refresh">
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.ACCRUAL_NEW)}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} />
                New Accrual
              </button>
            )}
          </div>
        </div>

        {/* â”€â”€ Filters â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search code or description..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          </div>
          <select
            value={filter.accrualType ?? ''}
            onChange={e => { setFilter(f => ({ ...f, accrualType: e.target.value || undefined, page: 1 })); setPage(1); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-2
                       bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none"
          >
            <option value="">All Types</option>
            {ACCRUAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            value={filter.status ?? ''}
            onChange={e => { setFilter(f => ({ ...f, status: e.target.value || undefined, page: 1 })); setPage(1); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-2
                       bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none"
          >
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* â”€â”€ Table â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Clock4 size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No accrual schedules found</p>
            {canCreate && (
              <button onClick={() => navigate(AppRoutes.ACCRUAL_NEW)} className="mt-3 text-teal-600 dark:text-teal-400 text-sm hover:underline">
                Create your first accrual
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Total Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Periods</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Start Date</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">End Date</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                {canDelete && <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(item => (
                <tr key={item.id}
                    onClick={() => canView && navigate(AppRoutes.ACCRUAL_DETAIL.replace(':id', item.id))}
                    className={cn('hover:bg-teal-50 dark:hover:bg-slate-700/50 transition-colors', canView && 'cursor-pointer')}>
                  <td className="px-4 py-3 font-mono font-semibold text-teal-700 dark:text-teal-400">{item.scheduleCode}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300 max-w-[180px] truncate">{item.description}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs">{item.accrualType}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">{fmtAmt(item.totalAmount, item.currencyCode)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-semibold text-slate-700 dark:text-slate-300">{item.periodsRecognized}</span>
                    <span className="text-slate-400"> / {item.totalPeriods}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{fmtDate(item.startDate)}</td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{item.endDate ? fmtDate(item.endDate) : '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_COLORS[item.status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                      {item.status}
                    </span>
                  </td>
                  {canDelete && (
                    <td className="px-4 py-3 text-right">
                      {['Draft', 'Active'].includes(item.status) && (
                        <button onClick={() => handleCancel(item)}
                          title="Cancel"
                          className="p-1.5 rounded hover:bg-red-100 text-red-500 transition-colors">
                          <XCircle size={15} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* â”€â”€ Pagination â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">Page {page} of {totalPages} · {total.toLocaleString()} records</p>
          <div className="flex items-center gap-1">
            <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const n = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
              return (
                <button key={n} onClick={() => handlePageChange(n)}
                  className={cn('w-8 h-8 text-sm rounded transition-colors',
                    n === page ? 'bg-teal-600 text-white font-semibold' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'
                  )}>{n}</button>
              );
            })}
            <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={cancelling !== null}
        variant="warning"
        title="Cancel Accrual Schedule"
        message={<span>Cancel accrual schedule <strong>{cancelling?.scheduleCode}</strong>?</span>}
        confirmLabel="Yes, Cancel"
        cancelLabel="No"
        loading={cancelLoading}
        onConfirm={executeCancel}
        onCancel={() => setCancelling(null)}
      />
    </div>
  );
};

